import axios from 'axios';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });

// Set global axios default so ALL axios.create() instances (including inline ones) inherit the token
try {
  const u = JSON.parse(localStorage.getItem('npd_user'));
  if (u?.token) axios.defaults.headers.common['Authorization'] = 'Bearer ' + u.token;
} catch {}

// Attach auth token to every request
API.interceptors.request.use(config => {
  try {
    const u = JSON.parse(localStorage.getItem('npd_user'));
    if (u?.token) config.headers.Authorization = 'Bearer ' + u.token;
  } catch {}
  return config;
});

// On 401, clear session and send to login
API.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401 && !window.location.pathname.startsWith('/panel') && !window.location.pathname.startsWith('/review')) {
      localStorage.removeItem('npd_user');
      if (window.location.pathname !== '/login') window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const settingsAPI = {
  getAll: () => API.get('/settings'),
  getType: (type) => API.get(`/settings/${type}`),
  create: (type, value) => API.post('/settings', { type, value }),
  delete: (id) => API.delete(`/settings/${id}`),
};

export const itemsAPI = {
  search: (q, classification) => API.get('/items', { params: { search: q, classification } }),
  getAll: () => API.get('/items'),
  getForRecipes: () => API.get('/items', { params: { classification: 'RAW MATERIALS,INGREDIENTS' } }),
  create: (data) => API.post('/items', data),
  update: (id, data) => API.put(`/items/${id}`, data),
  delete: (id) => API.delete(`/items/${id}`),
  uploadCSV: (file) => { const fd = new FormData(); fd.append('file', file); return API.post('/items/upload', fd); },
  uploadExcel: (file) => { const fd = new FormData(); fd.append('file', file); return API.post('/items/upload-excel', fd); },
};

export const projectsAPI = {
  getAll: () => API.get('/projects'),
  get: (id) => API.get(`/projects/${id}`),
  create: (data) => API.post('/projects', data),
  update: (id, data) => API.put(`/projects/${id}`, data),
};

export const productsAPI = {
  getAll: (params) => API.get('/products', { params }),
  create: (data) => API.post('/products', data),
  update: (id, data) => API.put(`/products/${id}`, data),
  move: (id, project_id) => API.put(`/products/${id}/move`, { project_id }),
  getVersions: (productId) => API.get(`/products/${productId}/versions`),
  createVersion: (productId, data) => API.post(`/products/${productId}/versions`, data),
  getRecipe: (productId) => API.get(`/products/${productId}/recipe`),
  saveRecipe: (productId, data) => API.post(`/products/${productId}/recipe`, data),
  convertToSfg: (id, data) => API.post(`/products/${id}/convert-to-sfg`, data),
};

export const trialsAPI = {
  getAll: (versionId) => API.get(`/versions/${versionId}/trials`),
  create: (versionId, data) => API.post(`/versions/${versionId}/trials`, data),
  update: (id, data) => API.put(`/trials/${id}`, data),
  getMaterials: (trialId) => API.get(`/trials/${trialId}/materials?_t=${Date.now()}`),
  addMaterial: (trialId, data) => API.post(`/trials/${trialId}/materials`, data),
  updateMaterial: (id, data) => API.put(`/materials/${id}`, data),
  deleteMaterial: (id) => API.delete(`/materials/${id}`),
  softDelete: (id) => API.delete(`/trials/${id}/soft`),
  restore: (id) => API.put(`/trials/${id}/restore`),
  getDeleted: (productId) => API.get(`/products/${productId}/deleted-trials`),
  uploadTrialSheet: (versionId, file) => {
    const fd = new FormData(); fd.append('file', file);
    return API.post(`/versions/${versionId}/upload-trial-sheet`, fd);
  },
};

export const dashboardAPI = {
  getSummary: () => API.get('/dashboard/summary'),
};

export const costSheetAPI = {
  get: (productId) => API.get(`/products/${productId}/costsheet`),
  addPacking: (productId, data) => API.post(`/products/${productId}/costsheet/packing`, data),
  updatePacking: (id, data) => API.put(`/costsheet/packing/${id}`, data),
  deletePacking: (id) => API.delete(`/costsheet/packing/${id}`),
  updatePacking: (id, data) => API.put(`/costsheet/packing/${id}`, data),
  addOperation: (productId, data) => API.post(`/products/${productId}/costsheet/operations`, data),
  updateOperation: (id, data) => API.put(`/costsheet/operations/${id}`, data),
  deleteOperation: (id) => API.delete(`/costsheet/operations/${id}`),
  updateOperation: (id, data) => API.put(`/costsheet/operations/${id}`, data),
};

export const projectReportAPI = {
  getReport: (projectId) => API.get(`/projects/${projectId}/report`),
  getAdditionalCosts: (projectId) => API.get(`/projects/${projectId}/additional-costs`),
  addAdditionalCost: (projectId, data) => API.post(`/projects/${projectId}/additional-costs`, data),
  updateAdditionalCost: (id, data) => API.put(`/project-additional-costs/${id}`, data),
  deleteAdditionalCost: (id) => API.delete(`/project-additional-costs/${id}`),
};

export const authAPI = {
  login: (data) => API.post('/auth/login', data),
  getUsers: () => API.get('/users'),
  createUser: (data) => API.post('/users', data),
  updateUser: (id, data) => API.put(`/users/${id}`, data),
  deleteUser: (id) => API.delete(`/users/${id}`),
};
